import {Injectable} from '@angular/core';
import {Loan} from '../model/loan.model';
import {EmiStatus} from '../model/emi-status';
import * as moment from 'moment';
import {isDate} from 'moment';
import {PrepaymentStatus} from '../model/prepayment-status';
import {LoanStatusType} from '../model/loan-status.model';
import {LoanSettlementService} from './loan-settlement.service';
import {LoanSettlementStatus} from '../model/loan-settlement/LoanSettlementStatus';
import {LenderCommissionRateUtil} from '../utils/lender/lender-commission-rate.util';
import {add, multiply, round, subtract} from 'lodash';
import {ConfigService} from './config.service';
import {EmiSchedule} from '../model/emi-schedule.model';
import {LoanRestructureService} from './loan-restructure.service';

interface LenderReturn {
    amount: number;
    interest: number;
    paidDate?: Date;
}

@Injectable()
export class LenderReturnCalculator {
    private readonly loanSettlementService: LoanSettlementService;
    private readonly lenderCommissionRateUtil: LenderCommissionRateUtil;
    private readonly configService: ConfigService;
    private readonly loanRestructureService: LoanRestructureService;


    constructor(loanSettlementService: LoanSettlementService, lenderCommissionRateUtil: LenderCommissionRateUtil,
                configService: ConfigService, loanRestructureService: LoanRestructureService) {
        this.loanSettlementService = loanSettlementService;
        this.lenderCommissionRateUtil = lenderCommissionRateUtil;
        this.configService = configService;
        this.loanRestructureService = loanRestructureService;
    }

    public async getNetInterestEarnedForLender(loan: Loan, lenderId: string): Promise<number> {
        const lenderShare = this.getLenderShare(loan, lenderId);
        const paidEmis = this.getEmiReturns(loan.emi_schedule);
        const prepayments = this.getPrepayments(loan);
        const settlements = await this.getSettlements(loan);
        const restructures = await this.getRestructures(loan);
        return round(this.getNetInterestAmount([...paidEmis, ...prepayments, ...settlements, ...restructures]) * lenderShare, 2);
    }

    public getNetEmiAmountOverdueForLender(loan: Loan, lenderId: string): number {
        const lenderShare = this.getLenderShare(loan, lenderId);
        const dueEmis = this.getDueEmis(loan);
        return round(this.getNetAmountOverDue(dueEmis) * lenderShare, 2);
    }

    public getNetEmiInterestEarnedForLender(emiSchedule: EmiSchedule, lenderShare: number) {
        const paidEmis = this.getEmiReturns(emiSchedule);
        return round(this.getNetInterestAmount(paidEmis) * lenderShare, 2);
    }

    private getNetAmountOverDue(dueEmis): number {
        let commissionWithGst = 0;
        let totalEmiAmount = 0;
        dueEmis.map(lenderReturn => {
            if (lenderReturn) {
                const commissionRate = this.lenderCommissionRateUtil.getLenderCommissionBy(lenderReturn.paidDate) / 100;
                const commissionAmount = lenderReturn.amount * commissionRate;
                const gstAmount = multiply(commissionAmount, this.configService.getGstPercentage() / 100);
                commissionWithGst += commissionAmount + gstAmount;
                totalEmiAmount += lenderReturn.amount;
            }
        });
        return subtract(totalEmiAmount, commissionWithGst);
    }

    private getDueEmis(loan: Loan) {
        const outStandingDelay = loan.emi_delay_details.emi_delay.find(delay => !delay.is_delay_amount_paid);
        if (outStandingDelay && outStandingDelay.associatedEmis) {
            return outStandingDelay.associatedEmis.map(associatedEmi => {
                const foundEmi = loan.emi_schedule.find(emi => emi._id === associatedEmi);
                return <LenderReturn>{
                    amount: foundEmi.emi_amount,
                    paidDate: moment().toDate(),
                    interest: foundEmi.interest_payable
                };
            });
        }
    }

    private getLenderShare(loan: Loan, lenderId: string): number {
        const lenderInvestment = loan.lenders_invested.find(investment => investment.lender_id === lenderId);
        return lenderInvestment.invested_amount / loan.loan_fulfilled_amount;
    }

    private async getSettlements(loan: Loan): Promise<LenderReturn[]> {
        if (loan.loan_sanction_status !== LoanStatusType.ClosedSettlement) {
            return [] as LenderReturn[];
        } else {
            const loanSettlements = await this.loanSettlementService.getByLoanId(loan._id);
            return loanSettlements.map(loanSettlement => {
                if (loanSettlement.status === LoanSettlementStatus.complete || loanSettlement.status === LoanSettlementStatus.settlementMoneyReceived) {
                    const penalInterest = loanSettlement.oneTimeSettlement.penalInterest ? loanSettlement.oneTimeSettlement.penalInterest : 0;
                    return <LenderReturn>{
                        amount: 0,
                        interest: add(loanSettlement.oneTimeSettlement.interestAccrued ? loanSettlement.oneTimeSettlement.interestAccrued : 0, penalInterest)
                    };
                }
            });
        }
    }

    private getPrepayments(loan: Loan): LenderReturn[] {
        return loan.prepayment_details.details.map(payment => {
            if (payment.prepayment_status === PrepaymentStatus.PartpaymentCompleted || payment.prepayment_status === PrepaymentStatus.PrepaymentCompleted) {
                return <LenderReturn>{
                    amount: 0,
                    interest: payment.prepayment_interest
                };
            }
        });
    }

    private getEmiReturns(emiSchedule: EmiSchedule): LenderReturn[] {
        return emiSchedule.map(emi => {
            if ((emi.status === EmiStatus.Received || emi.status === EmiStatus.Completed)
                && moment().isSameOrAfter(emi.due_on)) {
                const penalInterest = emi.penalInterest ? emi.penalInterest : 0;
                return <LenderReturn>{
                    amount: emi.emi_amount + penalInterest,
                    interest: emi.interest_payable + penalInterest,
                    paidDate: emi.paid_on
                };
            }
        });
    }

    private getNetInterestAmount(lenderReturns: LenderReturn[]) {
        let commissionWithGst = 0;
        let totalInterest = 0;
        lenderReturns.map(lenderReturn => {
            if (lenderReturn) {
                const commissionRate = this.lenderCommissionRateUtil.getLenderCommissionBy(lenderReturn.paidDate) / 100;
                const commissionAmount = lenderReturn.amount * commissionRate;
                const gstAmount = multiply(commissionAmount, this.configService.getGstPercentage() / 100);
                commissionWithGst += commissionAmount + gstAmount;
                totalInterest += lenderReturn.interest;
            }
        });
        return subtract(totalInterest, commissionWithGst);
    }

    private async getRestructures(loan: Loan): Promise<LenderReturn[]> {
        if (!isDate(loan.loanRestructuredOn)) {
            return [] as LenderReturn[];
        }
        const loanRestructures = await this.loanRestructureService.getByLoanId(loan._id);
        return loanRestructures.map(loanRestructure => {
            return <LenderReturn>{
                amount: 0,
                interest: loanRestructure.upfrontPaymentInterest
            };
        });
    }
}

